/**
 * useViewerProductConfig — Runtime viewer hook.
 *
 * Fetches the public viewer configuration for a given company/product:
 * - Brand, support, files from /api/viewer/:cid/:pid/config (legacy)
 * - Tools and parts from /api/viewer/:cid/:pid/items (new Items API)
 *
 * Falls back to legacy config tools/parts if the /items endpoint fails.
 * Caches the result for the session lifetime (no re-fetch on re-render).
 * Falls back silently to defaults on network error — no user-visible error.
 */

import { useEffect, useRef, useState } from 'react';
import type {
  ViewerBrandConfig,
  ViewerFileConfig,
  ViewerPartConfig,
  ViewerSupportConfig,
  ViewerToolConfig,
} from '../server/admin/viewer-config/types';

// ── Types ────────────────────────────────────────────────────────

export interface ViewerProductConfig {
  brand: ViewerBrandConfig | null;
  parts: ViewerPartConfig[];
  tools: ViewerToolConfig[];
  support: ViewerSupportConfig | null;
  files: ViewerFileConfig[];
  isLoading: boolean;
}

// ── Session Cache ────────────────────────────────────────────────

const sessionCache = new Map<string, ViewerProductConfig>();

function getCacheKey(companyId: string, productId: string): string {
  return `${companyId}::${productId}`;
}

// ── API Base URL ─────────────────────────────────────────────────

function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || '';
}

// ── Hook ─────────────────────────────────────────────────────────

export function useViewerProductConfig(
  companyId: string | null,
  productId: string | null,
): ViewerProductConfig {
  const [state, setState] = useState<ViewerProductConfig>(() => {
    // Check session cache first
    if (companyId && productId) {
      const cached = sessionCache.get(getCacheKey(companyId, productId));
      if (cached) return cached;
    }
    return {
      brand: null,
      parts: [],
      tools: [],
      support: null,
      files: [],
      isLoading: !!(companyId && productId),
    };
  });

  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!companyId || !productId) {
      setState({ brand: null, parts: [], tools: [], support: null, files: [], isLoading: false });
      return;
    }

    const cacheKey = getCacheKey(companyId, productId);

    // Already fetched this combination
    if (fetchedRef.current === cacheKey) return;

    // Check session cache
    const cached = sessionCache.get(cacheKey);
    if (cached) {
      fetchedRef.current = cacheKey;
      setState(cached);
      return;
    }

    let cancelled = false;

    async function fetchConfig() {
      try {
        const baseUrl = getApiBaseUrl();

        // Fetch both endpoints in parallel
        const [configResponse, itemsResponse] = await Promise.all([
          fetch(
            `${baseUrl}/api/viewer/${encodeURIComponent(companyId!)}/` +
            `${encodeURIComponent(productId!)}/config`,
          ),
          fetch(
            `${baseUrl}/api/viewer/${encodeURIComponent(companyId!)}/` +
            `${encodeURIComponent(productId!)}/items`,
          ).catch(() => null), // Items endpoint failure is non-fatal
        ]);

        if (cancelled) return;

        // Parse config response (brand, support, files, legacy tools/parts)
        let brand: ViewerBrandConfig | null = null;
        let support: ViewerSupportConfig | null = null;
        let files: ViewerFileConfig[] = [];
        let legacyParts: ViewerPartConfig[] = [];
        let legacyTools: ViewerToolConfig[] = [];

        if (configResponse.ok) {
          const configJson = await configResponse.json();
          const data = configJson.data;
          brand = data?.brandConfig ?? null;
          support = data?.supportConfig ?? null;
          files = data?.filesConfig ?? [];
          legacyParts = data?.partsConfig ?? [];
          legacyTools = data?.toolsConfig ?? [];
        }

        if (cancelled) return;

        // Parse items response (tools/parts from new Items API)
        let itemsTools: ViewerToolConfig[] | null = null;
        let itemsParts: ViewerPartConfig[] | null = null;

        if (itemsResponse && itemsResponse.ok) {
          const itemsJson = await itemsResponse.json();
          const itemsData = itemsJson.data;
          if (itemsData) {
            itemsTools = itemsData.tools ?? null;
            itemsParts = itemsData.parts ?? null;
          }
        }

        if (cancelled) return;

        // Prefer items from /items endpoint; fall back to legacy /config JSONB
        const result: ViewerProductConfig = {
          brand,
          tools: (itemsTools && itemsTools.length > 0) ? itemsTools : legacyTools,
          parts: (itemsParts && itemsParts.length > 0) ? itemsParts : legacyParts,
          support,
          files,
          isLoading: false,
        };

        sessionCache.set(cacheKey, result);
        fetchedRef.current = cacheKey;
        setState(result);
      } catch {
        if (cancelled) return;

        // Network error — silently fall back to defaults
        const defaults: ViewerProductConfig = {
          brand: null,
          parts: [],
          tools: [],
          support: null,
          files: [],
          isLoading: false,
        };
        sessionCache.set(cacheKey, defaults);
        fetchedRef.current = cacheKey;
        setState(defaults);
      }
    }

    setState(prev => ({ ...prev, isLoading: true }));
    fetchConfig();

    return () => {
      cancelled = true;
    };
  }, [companyId, productId]);

  return state;
}
